<template>
    <div
        class="l-ss-c-image-search-container">
        <input
            ref="image-picker"
            accept="image/*,application/*"
            style="display: none;"
            type="file"
            @change="onFileChange">
        <div
            v-if="openFilePicker"
            class="l-ss-c-results-loader">
            <pulse-dots/>
        </div>
        <div
            v-if="!isImageLoaded"
            class="l-ss-c-image-search-select">
            <i
                v-show="isProcessing"
                class="l-ss-c-busy-loader">
            </i>
            <p
                v-if="error"
                class="l-ss-c-image-search-error">
                {{ error || `Failed to process an image` }}
            </p>
            <div class="l-ss-c-image-search-row">
                <div class="l-ss-c-image-search-column">
                    <div
                        ref="drop-zone"
                        :class="{ 'l-ss-c-image-search-drop-zone-active': dropZoneActive }"
                        class="l-ss-c-image-search-drop-zone">
                        <p
                            v-if="$screen.isMobile === false"
                            class="l-ss-c-text-caption">
                            {{ $t('imageSearch.dropTitle') }}
                        </p>
                        <button
                            class="l-ss-c-button l-ss-c-button-large"
                            @click="onOpenFilePicker($event)">
                            {{ $t('imageSearch.selectFileButton') }}
                        </button>
                    </div>
                </div>
                <div class="l-ss-c-image-search-column">
                    <div class="l-ss-c-image-search-link-zone">
                        <p class="l-ss-c-text-caption">{{ $t('imageSearch.pasteLink') }}</p>
                        <input
                            :placeholder="$t('imageSearch.pasteLinkPlaceholder')"
                            class="l-ss-c-input l-ss-c-image-search-link-input"
                            type="text"
                            @input="onLinkChange">
                    </div>
                </div>
            </div>
        </div>
        <template v-else>
            <div class="l-ss-c-image-search-row">
                <div class="l-ss-c-image-search-column">
                    <cropper
                        :canvas="{ maxHeight: 350, maxWidth: 500 }"
                        :resize-image="false"
                        :src="baseImage"
                        :stencil-props="cropperStencilProps"
                        class="l-ss-c-image-search-cropper"
                        @change="onCropChange"/>
                </div>
                <div class="l-ss-c-image-search-column">
                    <div
                        v-if="isImageSearchLoader"
                        class="l-ss-c-image-search-loader-container">
                        <pulse-dots/>
                    </div>

                    <template v-else>
                        <p class="l-ss-c-text-caption">{{ $t('imageSearch.cropHelp') }}</p>

                        <button
                            class="l-ss-c-button l-ss-c-button-large"
                            @click="handleSearchCropped">
                            {{ $t('imageSearch.handleSearchButton') }}
                        </button>
                    </template>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
    import { IMAGE_PROXY_URL } from '../../config';
    import debounce from '../helpers/debounce';
    import PulseDots from '@/core/components/pulse-dots';

    const HANDLE_LINK_CHANGE_DELAY = 300;

    export default {
        components: {
            Cropper: () => import('vue-advanced-cropper').then(({ Cropper }) => Cropper),
            PulseDots,
        },
        props: {
            baseImage: {
                type: [String, null],
                default: null,
            },
            baseImageResult: {
                type: [String, null],
                default: null,
            },
        },
        data() {
            return {
                error: null,
                isReady: false,
                isProcessing: false,
                linkTimer: null,
                dropZone: null,
                dropZoneActive: false,
                openFilePicker: false,
                cropperStencilProps: {
                    handlers: {
                        eastNorth: true,
                        north: false,
                        westNorth: true,
                        west: false,
                        westSouth: true,
                        south: false,
                        eastSouth: true,
                        east: false,
                    },
                },
            };
        },
        computed: {
            isImageLoaded() {
                return this.isReady === true && this.isProcessing === false && this.error === null;
            },

            isImageSearchLoader() {
                return this.$root.props.store.state.ui.isImageSearchLoader;
            },
        },
        watch: {
            isImageLoaded(value) {
                if (!value) {
                    this.$nextTick(this.addDragListeners);
                } else {
                    document.addEventListener('keypress', this.searchOnEnter);
                }
            },
            baseImage(value) {
                if (!value) {
                    this.isReady = null;
                    this.error = null;
                    this.isProcessing = false;
                    this.$refs['image-picker'].value = '';
                }
            },
        },
        mounted() {
            this.addDragListeners();
        },
        methods: {
            addDragListeners() {
                const dropContainer = this.$refs['drop-zone'];
                dropContainer.ondragover = dropContainer.ondragenter = (event) => {
                    event.preventDefault();
                    this.dropZoneActive = true;
                };
                dropContainer.ondragleave = (event) => {
                    event.preventDefault();
                    this.dropZoneActive = false;
                };
                dropContainer.ondrop = (event) => {
                    event.preventDefault();
                    this.imageSearchButtonSelectFileClick(event);
                    if (event.dataTransfer.files.length === 0) {
                        this.error = this.$t('imageSearch.errors.noImage');
                        return;
                    }
                    this.readAsBase64(event.dataTransfer.files[0])
                        .then(base64 => {
                            this.processImageResult(true, base64);
                        })
                        .catch(error => {
                            this.processImageResult(false, error);
                        });
                };
            },
            readAsBase64(file) {
                return new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onloadend = function () {
                        resolve(reader.result);
                    };
                    reader.onerror = function (error) {
                        reject(error);
                    };
                    reader.readAsDataURL(file);
                });
            },
            onOpenFilePicker(event) {
                this.isReady = null;
                this.error = null;
                this.isProcessing = false;
                this.$refs['image-picker'].click();
                this.imageSearchButtonSelectFileClick(event);
            },
            imageSearchButtonSelectFileClick(event) {
                this.$emit('image-search-button-select-file-click', { type: 'imageSearchButtonSelectFileClick', event });
            },
            processImageResult(isSuccess, response) {
                if (isSuccess) {
                    this.isProcessing = false;
                    this.isReady = true;
                    this.$emit('base64-change', response);
                } else {
                    const error = response || 'Failed to load an image';
                    this.error = String(error.message || error.statusText || error);
                    this.isProcessing = false;
                    this.isReady = true;
                }
            },
            onFileChange(event) {
                this.isReady = null;
                this.error = null;
                this.isProcessing = true;
                this.openFilePicker = true;
                this.readAsBase64(event.target.files[0])
                    .then(base64 => {
                        this.openFilePicker = false;
                        this.processImageResult(true, base64);
                    })
                    .catch(error => {
                        this.openFilePicker = false;
                        this.processImageResult(false, error);
                    });
            },
            onCropChange(params) {
                this.$emit('crop-change', params);
            },
            onLinkChange(event) {
                const imageUrlRegex = /^https?:\/\/.*\/.*\.(png|gif|webp|jpeg|jpg)\??.*$/gmi;
                this.$refs['image-picker'].value = '';
                const value = String(event.target.value).trim();
                const DELAY = 5000;
                if (this.linkTimer) {
                    clearTimeout(this.linkTimer);
                }
                this.linkTimer = setTimeout( async () => {
                    if (!value) {
                        this.isReady = null;
                        this.error = null;
                        this.isProcessing = false;
                        return;
                    }
                    if (!/^http/.test(value)) {
                        this.isReady = null;
                        this.error = this.$t('imageSearch.errors.invalidUrlHTTP');
                        this.isProcessing = false;
                        debounce(this.imageSearchUseIncorrectLinkClick(event, value), DELAY);
                        return;
                    }
                    if (!value.match(imageUrlRegex)) {
                        this.isReady = null;
                        this.error = this.$t('imageSearch.errors.invalidUrl');
                        this.isProcessing = false;
                        debounce(this.imageSearchUseIncorrectLinkClick(event, value), DELAY);
                        return;
                    }
                    this.isReady = null;
                    this.error = null;
                    this.isProcessing = true;
                    const self = this;
                    try {
                        const fileExtension = value.match(/\.[0-9a-z]+(?:\?[^\s]+)?$/i)[0].replace('.', 'image/');
                        const blob = await self.loadImageToBlobByUrl(value);
                        this.$emit('image-search-use-correct-link-click', { type: 'imageSearchUseCorrectLink', event, text: value });
                        this.readAsBase64(blob)
                            .then(base64 => {
                                const modifyBase64String = base64.replace('application/octet-stream', fileExtension);
                                self.processImageResult(true, modifyBase64String);
                            })
                            .catch(error => {
                                self.processImageResult(false, error);
                            });
                    } catch (error) {
                        self.error = this.$t('imageSearch.errors.invalidUrl');
                        self.isProcessing = false;
                        self.isReady = true;
                    }
                }, HANDLE_LINK_CHANGE_DELAY);
            },
            imageSearchUseIncorrectLinkClick(event, value) {
                this.$emit('image-search-use-incorrect-link-click', { type: 'imageSearchUseIncorrectLink', text: value, event });
            },
            handleSearchCropped(event) {
                document.removeEventListener('keypress', this.searchOnEnter);
                this.$emit('image-search-button-search-click', { type: 'imageSearchButtonSearch', event });
                this.$emit('search');
            },
            searchOnEnter(event) {
                if (event.code === 'Enter') {
                    this.handleSearchCropped();
                }
            },
            loadImageToBlobByUrl(url) {
                const paramsPart = {
                    i: url,
                };
  
                return fetch(`${IMAGE_PROXY_URL}?${new URLSearchParams(paramsPart).toString()}`)
                    .then(response => response.body)
                    .then(stream => new Response(stream))
                    .then(response => response.blob());
            },
        },
    };
</script>
